<template>
    <div class="monitor-page">
        <!-- Tab 切换 -->
        <van-tabs
            v-model="activeTab"
            @change="onTabChange"
            sticky
            color="#1989fa"
            line-width="28"
            line-height="3"
            :animated="true"
            title-active-color="#1989fa"
            title-inactive-color="#323233"
        >
            <van-tab title="LED广告监测" :name="1"></van-tab>
            <van-tab title="户外广告监测" :name="2"></van-tab>
        </van-tabs>

        <!-- 列表容器（滚动监听触底加载） -->
        <div class="list-container" @scroll="handleScroll" ref="listContainer">
            <div v-if="loading && monitorList.length === 0" class="loading-tip">
                <van-loading size="24px">数据加载中...</van-loading>
            </div>
            <div v-else-if="monitorList.length === 0" class="empty-tip">
                <van-icon name="info-o" size="56" color="#d8d8d8" />
                <span class="empty-title">暂无符合条件的监测数据</span>
            </div>
            <div v-else class="monitor-list">
                <div
                    v-for="(item, index) in monitorList"
                    :key="index"
                    class="monitor-item"
                    :class="+item.state === 1 ? 'is-warn' : 'is-ok'"
                    @click="goToDetail(item.illegalId)"
                >
                    <div class="item-accent"></div>
                    <div class="item-body">
                        <!-- 顶部：屏体名称 + 广告主 + 状态 chip -->
                        <div class="item-header">
                            <div class="title-wrap">
                                <span class="screen-name">
                                    {{ +queryType === 1 ? item.screenName : item.mediaName }}
                                </span>
                                <span v-if="item.contentCustomerName" class="customer-name">
                                    {{ item.contentCustomerName }}
                                </span>
                            </div>
                            <span class="state-chip" :class="+item.state === 1 ? 'chip-warn' : 'chip-ok'">
                                {{ +item.state === 1 ? '未处理' : '已处理' }}
                            </span>
                        </div>

                        <!-- 行业 -->
                        <div
                            v-if="item.contentCustomerMainIndustry || item.contentCustomerIndustry"
                            class="item-row"
                        >
                            <van-icon name="label-o" size="13" color="#969799" />
                            <span class="row-text">
                                <span v-if="item.contentCustomerMainIndustry">{{ item.contentCustomerMainIndustry }}</span>
                                <span v-if="item.contentCustomerMainIndustry && item.contentCustomerIndustry"> · </span>
                                <span v-if="item.contentCustomerIndustry">{{ item.contentCustomerIndustry }}</span>
                            </span>
                        </div>

                        <!-- 位置 · 时间 -->
                        <div class="item-row">
                            <van-icon name="location-o" size="13" color="#1989fa" />
                            <span class="row-text">{{ (item.city || '') + (item.district || '') || '-' }}</span>
                            <span class="row-divider"></span>
                            <van-icon name="clock-o" size="13" color="#969799" />
                            <span class="row-text">{{ formatTime(item.addTime) }}</span>
                        </div>

                        <!-- LED 播放统计 -->
                        <div v-if="+queryType === 1" class="item-stats">
                            <div class="stat-pill">
                                <van-icon name="play-circle-o" size="13" color="#1989fa" />
                                <span class="stat-label">播放次数</span>
                                <span class="stat-value">{{ item.totalPlayCount || 0 }}</span>
                            </div>
                            <div class="stat-pill">
                                <van-icon name="underway-o" size="13" color="#07c160" />
                                <span class="stat-label">累计秒数</span>
                                <span class="stat-value">{{ item.totalSecond || 0 }}s</span>
                            </div>
                        </div>

                        <!-- 备注 -->
                        <div v-if="item.remarkContent" class="item-remark">
                            <van-icon name="warning-o" />
                            <span>{{ item.remarkContent }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="loadingMore" class="loading-more-tip">
                    <van-loading size="20px">加载更多...</van-loading>
                </div>
                <div v-if="noMoreData && monitorList.length > 0" class="no-more-tip">— 已加载全部数据 —</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MonitorView',
    data() {
        return {
            activeTab: 1,            // 当前激活的 tab：1-LED / 2-户外
            queryType: 1,            // 1-LED, 2-户外
            state: 1,                // 1-未处理 / 2-已处理（驱动 stateList）
            savedScrollTop: 0,       // keep-alive 滚动恢复
            monitorList: [],
            loading: false,
            loadingMore: false,
            noMoreData: false,
            filterForm: {
                pageNum: 0,
                pageSize: 10,
                stateList: [1]       // 默认只看未处理
            }
        };
    },
    mounted() {
        this.getMonitorData();
    },
    activated() {
        // keep-alive 恢复时还原滚动位置
        if (this.$refs.listContainer && this.savedScrollTop > 0) {
            this.$refs.listContainer.scrollTop = this.savedScrollTop;
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.$refs.listContainer) {
            this.savedScrollTop = this.$refs.listContainer.scrollTop;
        }
        next();
    },
    watch: {
        state(newVal) {
            // 1 未处理 → [1]；其它 → 已处理 [2,3]
            this.filterForm.stateList = newVal === 1 ? [1] : [2, 3];
        }
    },
    methods: {
        // Tab 切换：重置列表 + 重新加载
        onTabChange(name) {
            this.queryType = name;
            this.monitorList = [];
            this.noMoreData = false;
            this.filterForm.pageNum = 0;
            this.getMonitorData();
        },

        // 时间格式化：yyyy-MM-dd HH:mm
        formatTime(timestamp) {
            if (!timestamp) return '-';
            const date = new Date(timestamp);
            const p = (n) => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}`;
        },

        // 拉取监测数据
        async getMonitorData(filterParams = {}, isLoadMore = false) {
            if (isLoadMore) {
                this.loadingMore = true;
            } else {
                this.loading = true;
                if (!this.monitorList.length || filterParams.pointName || filterParams.updateTime) {
                    this.monitorList = [];
                    this.noMoreData = false;
                }
            }

            try {
                const params = { ...this.filterForm, ...filterParams };
                const url = +this.queryType === 1
                    ? '/monitor/monitorillegal/queryPageList'
                    : '/monitor/monitorillegalad/queryPageList';
                const res = await this.$post(url, params);
                if (res.rescode === 200) {
                    const arr = (res.data && res.data.dataList) || [];
                    this.monitorList = isLoadMore ? [...this.monitorList, ...arr] : arr;
                    const total = (res.data && res.data.totalCount) || 0;
                    this.noMoreData = this.monitorList.length >= total;
                } else {
                    this.$toast(res.msg);
                    if (isLoadMore) this.filterForm.pageNum -= 1;
                }
            } catch (e) {
                console.error('请求失败：', e);
                this.$toast('数据加载失败，请重试');
                if (isLoadMore) this.filterForm.pageNum -= 1;
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },

        // 跳转详情
        goToDetail(illegalId) {
            this.$router.push({
                path: '/monitorInfo',
                query: { illegalId, queryType: this.queryType }
            });
        },

        // 触底加载
        handleScroll() {
            if (this.loadingMore || this.noMoreData || this.loading) return;
            const c = this.$refs.listContainer;
            if (!c) return;
            if (c.scrollTop + c.clientHeight >= c.scrollHeight - 20) {
                this.filterForm.pageNum += 1;
                this.getMonitorData({}, true);
            }
        }
    }
};
</script>

<style scoped>
.monitor-page {
    height: 100vh;
    background: #f4f6f9;
    display: flex;
    flex-direction: column;
}
.monitor-page ::v-deep .van-tabs {
    flex-shrink: 0;
}

/* 列表容器 */
.list-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    box-sizing: border-box;
}

.monitor-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 50px;
}

/* 加载/空状态 */
.loading-tip,
.empty-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    gap: 10px;
    color: #969799;
    font-size: 14px;
}
.empty-title {
    font-size: 14px;
    color: #969799;
}
.loading-more-tip,
.no-more-tip {
    text-align: center;
    padding: 18px 0;
    color: #c8c9cc;
    font-size: 12px;
}

/* 卡片 */
.monitor-item {
    position: relative;
    display: flex;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    cursor: pointer;
}
.monitor-item:active {
    transform: scale(0.99);
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
}

.item-accent {
    width: 4px;
    flex-shrink: 0;
}
.monitor-item.is-warn .item-accent { background: #ee0a24; }
.monitor-item.is-ok .item-accent { background: #07c160; }

.item-body {
    flex: 1;
    min-width: 0;
    padding: 12px 12px 12px 14px;
}

/* 头部 */
.item-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}
.title-wrap {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.screen-name {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-all;
}
.customer-name {
    font-size: 12px;
    color: #969799;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 状态标签 */
.state-chip {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 8px;
    font-size: 11px;
    border-radius: 10px;
    line-height: 1;
}
.chip-warn { background: #fff1f0; color: #ee0a24; }
.chip-ok { background: #e6f7ec; color: #07c160; }

/* 行信息 */
.item-row {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12.5px;
    color: #323233;
    line-height: 1.5;
    margin-top: 4px;
}
.row-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.row-divider {
    width: 1px;
    height: 10px;
    background: #ebedf0;
    margin: 0 4px;
    flex-shrink: 0;
}

/* LED 播放统计 */
.item-stats {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #f2f3f5;
}
.stat-pill {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    background: #f7f8fa;
    border-radius: 6px;
    font-size: 12px;
    min-width: 0;
}
.stat-label {
    color: #969799;
    flex-shrink: 0;
}
.stat-value {
    color: #1a1a1a;
    font-weight: 600;
    margin-left: auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* 备注 */
.item-remark {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    padding: 6px 8px;
    background: #fff7e6;
    border-radius: 6px;
    font-size: 12px;
    color: #ff976a;
    line-height: 1.5;
    margin-top: 8px;
}

@media (max-width: 768px) {
    .list-container {
        padding: 10px;
    }
    .monitor-list {
        gap: 8px;
    }
}
</style>
